import { useState, useEffect, useCallback } from 'react'
import { drmClientService } from '@/services/drmClientService'

interface DRMSessionOptions {
  cosmkId?: string
  userInfo?: {
    userId: string
    sessionId: string
  }
  sessionInfo?: any
  enabled: boolean
}

interface DRMState {
  status: 'initializing' | 'ready' | 'error' | 'blocked'
  supportedSystems: string[]
  activeDRM?: string
  error?: string
}

interface LicenseInfo {
  licenseId: string
  drmSystem: string
  issuedAt: string
  expiresAt: string
  permissions: any
  forensicId: string
}

export const useDRMSession = (options: DRMSessionOptions) => {
  const { cosmkId, userInfo, sessionInfo, enabled } = options

  const [drmState, setDrmState] = useState<DRMState>({
    status: 'initializing',
    supportedSystems: []
  })

  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize DRM capabilities
  useEffect(() => {
    if (!enabled) {
      setDrmState({
        status: 'blocked',
        supportedSystems: [],
        error: 'DRM not enabled'
      })
      return
    }

    const initializeDRMCapabilities = async () => {
      try {
        // Wait for DRM client service to initialize
        await new Promise(resolve => setTimeout(resolve, 100))
        
        const capabilities = drmClientService.getDRMCapabilities()
        const supportedSystems = Object.entries(capabilities)
          .filter(([key, value]) => key !== 'userAgent' && key !== 'platform' && value)
          .map(([key]) => key)

        if (supportedSystems.length === 0) {
          setDrmState({
            status: 'error',
            supportedSystems: [],
            error: 'No supported DRM systems found'
          })
          return
        }

        const optimalDRM = drmClientService.getOptimalDRMSystem()
        
        setDrmState({
          status: 'ready',
          supportedSystems,
          activeDRM: optimalDRM || undefined
        })

        setIsInitialized(true)
        console.log('DRM session initialized', { supportedSystems, optimalDRM })

      } catch (error) {
        console.error('DRM initialization failed:', error)
        setDrmState({
          status: 'error',
          supportedSystems: [],
          error: error instanceof Error ? error.message : 'DRM initialization failed'
        })
      }
    }

    initializeDRMCapabilities()
  }, [enabled])

  // Initialize DRM session for specific content
  const initializeDRM = useCallback(async () => {
    if (!enabled || !cosmkId || !userInfo || !isInitialized) {
      return
    }

    try {
      setDrmState(prev => ({ ...prev, status: 'initializing' }))

      // Check if we already have a valid license
      const existingLicense = drmClientService.getLicense(cosmkId)
      if (existingLicense && drmClientService.isLicenseValid(cosmkId)) {
        setLicenseInfo(existingLicense)
        setDrmState(prev => ({ ...prev, status: 'ready' }))
        console.log('Using existing valid license:', existingLicense.licenseId)
        return
      }

      // Check concurrent streams before requesting license
      const concurrentAllowed = await drmClientService.checkConcurrentStreams(userInfo)
      if (!concurrentAllowed) {
        throw new Error('Maximum concurrent streams reached')
      }

      // Request new license
      console.log('Requesting new DRM license for:', cosmkId)
      const licenseResponse = await drmClientService.requestLicense(cosmkId, userInfo)
      
      if (!licenseResponse.success) {
        throw new Error(licenseResponse.error || 'License request failed')
      }

      if (licenseResponse.licenseInfo) {
        setLicenseInfo(licenseResponse.licenseInfo)
        setDrmState(prev => ({ ...prev, status: 'ready' }))
        console.log('DRM license acquired successfully:', licenseResponse.licenseInfo.licenseId)
      }

    } catch (error) {
      console.error('DRM session initialization failed:', error)
      setDrmState(prev => ({
        ...prev,
        status: 'error',
        error: error instanceof Error ? error.message : 'DRM session failed'
      }))
    }
  }, [enabled, cosmkId, userInfo, isInitialized])

  // Handle DRM errors
  const handleDRMError = useCallback((error: string) => {
    console.error('DRM Error:', error)
    setDrmState(prev => ({
      ...prev,
      status: 'error',
      error
    }))

    // Revoke license on security violations
    if (licenseInfo && (
      error.includes('Security violation') ||
      error.includes('screen recording') ||
      error.includes('unauthorized')
    )) {
      drmClientService.revokeLicense(licenseInfo.licenseId)
      setLicenseInfo(null)
    }
  }, [licenseInfo])

  // Refresh license
  const refreshLicense = useCallback(async () => {
    if (!cosmkId || !userInfo || !licenseInfo) {
      return false
    }

    try {
      console.log('Refreshing DRM license:', licenseInfo.licenseId)
      const refreshResponse = await drmClientService.refreshLicense(
        cosmkId,
        licenseInfo.licenseId,
        userInfo
      )

      if (refreshResponse.success && refreshResponse.licenseInfo) {
        setLicenseInfo(refreshResponse.licenseInfo)
        setDrmState(prev => ({ ...prev, status: 'ready', error: undefined }))
        console.log('License refreshed successfully')
        return true
      } else {
        throw new Error(refreshResponse.error || 'License refresh failed')
      }

    } catch (error) {
      console.error('License refresh failed:', error)
      handleDRMError(error instanceof Error ? error.message : 'License refresh failed')
      return false
    }
  }, [cosmkId, userInfo, licenseInfo, handleDRMError])

  // Revoke license
  const revokeLicense = useCallback(async () => {
    if (!licenseInfo) {
      return false
    }

    try {
      const success = await drmClientService.revokeLicense(licenseInfo.licenseId)
      if (success) {
        setLicenseInfo(null)
        setDrmState(prev => ({ ...prev, status: 'blocked' }))
        console.log('License revoked successfully')
      }
      return success

    } catch (error) {
      console.error('License revocation failed:', error)
      return false
    }
  }, [licenseInfo])

  // Check license validity periodically
  useEffect(() => {
    if (!enabled || !cosmkId || !licenseInfo) {
      return
    }

    const checkLicenseValidity = () => {
      const isValid = drmClientService.isLicenseValid(cosmkId)
      if (!isValid) {
        console.warn('License expired for content:', cosmkId)
        setDrmState(prev => ({
          ...prev,
          status: 'error',
          error: 'License has expired'
        }))
        setLicenseInfo(null)
      }
    }

    // Check immediately and then every 30 seconds
    checkLicenseValidity()
    const interval = setInterval(checkLicenseValidity, 30000)

    return () => clearInterval(interval)
  }, [enabled, cosmkId, licenseInfo])

  // Cleanup expired licenses periodically
  useEffect(() => {
    if (!enabled) return

    const cleanupInterval = setInterval(() => {
      drmClientService.cleanupExpiredLicenses()
    }, 60000) // Every minute

    return () => clearInterval(cleanupInterval)
  }, [enabled])

  // Get DRM session status
  const getSessionStatus = useCallback(() => {
    return {
      isReady: drmState.status === 'ready',
      hasValidLicense: licenseInfo && drmClientService.isLicenseValid(cosmkId || ''),
      canPlay: drmState.status === 'ready' && licenseInfo && drmClientService.isLicenseValid(cosmkId || ''),
      error: drmState.error
    }
  }, [drmState, licenseInfo, cosmkId])

  // Get device information
  const getDeviceInfo = useCallback(() => {
    return drmClientService.getDeviceFingerprint()
  }, [])

  // Get DRM capabilities
  const getDRMCapabilities = useCallback(() => {
    return drmClientService.getDRMCapabilities()
  }, [])

  return {
    drmState,
    licenseInfo,
    isInitialized,
    initializeDRM,
    handleDRMError,
    refreshLicense,
    revokeLicense,
    getSessionStatus,
    getDeviceInfo,
    getDRMCapabilities
  }
}